/**
 * SINGLE SOURCE OF TRUTH for campaign lifecycle status.
 *
 * Why this module exists: `campaign.status` used to be interpreted by THREE
 * separate normalizers with three different rule sets —
 *   - AdminCampaignCard.normalizeStatus   (pill COLOUR)
 *   - CampaignStatusSelect                (pill TEXT / <select> value)
 *   - CampaignDetailsModal.formatStatusLabel (modal label, raw passthrough)
 * They disagreed on `'archive'`: the colour normalizer mapped it to archived
 * (GREY) while the select's lookup had no `archive` key and fell back to
 * 'active', so one card rendered a GREY pill reading "Active" and its own
 * detail modal printed "Archive". Users could not tell the real status.
 *
 * Every read site must now consume `normalizeCampaignLifecycleStatus` (for
 * bucketing/colour) or `formatCampaignStatusLabel` (for display text), so the
 * pill and the modal can never contradict each other again.
 *
 * Contract note: `CampaignMetrics.status` is declared as
 * `'active' | 'paused' | 'archived' | 'completed'`, but real payloads also
 * carry `'archive'`, `'stopped'` and transitional dots-statuses
 * ("enriching…"). The aliases below are deliberate defence against those.
 */

/** The lifecycle buckets every campaign-status read site resolves to. */
export type CampaignLifecycleStatus = 'active' | 'paused' | 'archived';

/**
 * Normalize a raw campaign status into a lifecycle bucket.
 *
 * Returns `null` when the value carries no lifecycle meaning — callers MUST
 * then default to `'active'` (`?? 'active'`) rather than inventing a bucket.
 * That is what makes DONE/COMPLETED, transitional dots-statuses and
 * unrecognized values all read as Active.
 *
 * DONE/COMPLETED is FINISHED-but-still-live, not retired: it reads Active
 * (green, reachable). Only an explicit "archived"/"archive" retires a campaign.
 */
export const normalizeCampaignLifecycleStatus = (
  value: unknown
): CampaignLifecycleStatus | null => {
  if (value == null) return null;
  const raw = String(value).trim().toLowerCase();
  if (!raw) return null;
  // Transitional/system dots-statuses ("enriching…", "writing message…") are not
  // lifecycle states.
  if (raw.includes('...') || raw.includes('…')) return null;
  // 'archive' is an alias for 'archived' — the ONE alias the three old
  // normalizers disagreed about, so it is handled in one place now.
  if (raw === 'archived' || raw === 'archive') return 'archived';
  if (raw === 'paused' || raw === 'stopped') return 'paused';
  if (raw === 'active') return 'active';
  // done / completed / anything unrecognized: no lifecycle meaning → Active.
  return null;
};

/**
 * Human-readable status label for a pill or modal.
 *
 * Every status word in the UI comes from here, so a card's pill and the detail
 * modal it opens can never print different words for one campaign. Values that
 * share a lifecycle bucket but read differently to a user keep their own label
 * (Done, Stopped) so the distinction isn't lost — only the WORD is specialised,
 * the bucket stays Active/Paused/Archived for colour and filtering.
 */
export const formatCampaignStatusLabel = (value: unknown): string => {
  const raw = value == null ? '' : String(value).trim().toLowerCase();
  // Display names that carry more meaning than their lifecycle bucket.
  if (raw === 'asking to human') return 'Asking to human';
  if (raw === 'stopped') return 'Stopped';
  if (raw === 'done' || raw === 'completed') return 'Done';
  const normalized = normalizeCampaignLifecycleStatus(value);
  if (!normalized) return 'Active';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

/**
 * True when the raw status is system-managed and therefore NOT user-editable.
 * "stopped" / "asking to human" are lifecycle states the engine drives; the
 * status dropdown must render them read-only instead of offering to "keep" them.
 */
export const isSystemManagedCampaignStatus = (value: unknown): boolean => {
  const raw = value == null ? '' : String(value).trim().toLowerCase();
  return raw === 'stopped' || raw === 'asking to human';
};
